/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';

import { BuilderUserContext, FilterNames, FilterRestService } from '@gh/builder-shared';
import {
  ContactCustomerType,
  LeadBoardFilter,
  LeadBoardListItemParams,
  LeadBoardRestService,
  SalesFolderStatus,
  UserRestService,
} from '@gh/builder-shared-data';
import {
  dictionaryItemFromDto,
  DictionaryService,
  getRouteQueryParam, getRouteQueryParams,
  globalDictionaries,
  InitDialogViewService,
  InitViewService,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { DialogRouteSnapshot } from '@gh/core-ui';
import { map$, switchMap$ } from '@gh/rx/operators';

import { concat, map, partition, reverse, sortBy } from 'lodash';
import { Observable } from 'rxjs';
import {
  isSalespersonSelectItemInactive,
  LeadBoardCardMoveDialogConfig,
  LeadBoardListItem,
  SalespersonSelectItem,
  salespersonSelectItemMapper,
  UserListItem,
} from './lead-board.model';
import { combineLatest$ } from '@gh/rx';
import { getBool, sortByStringField } from '@gh/core-util';

@Injectable()
export class LeadBoardBusinessService
  implements InitViewService, InitDialogViewService<LeadBoardCardMoveDialogConfig> {

  constructor(private restService: LeadBoardRestService,
              private userRestService: UserRestService,
              private userContext: BuilderUserContext,
              private filterService: FilterRestService,
              private dictionaryService: DictionaryService) {
  }

  initView(route: ActivatedRouteSnapshot): any {
    return combineLatest$([this.filterService.get(FilterNames.leadsBord),
                            this.dictionaryService.getDictionaryFromGlobal(globalDictionaries.priority),
                          ]).pipe(switchMap$(([filter, priorities]) => {
      const salesPersonId = getRouteQueryParam(route, 'salesPersonId');
      const showCompleted = getRouteQueryParam(route, 'showCompleted');
      const showActive = getRouteQueryParam(route, 'showActive');
      const showInactive = getRouteQueryParam(route, 'showInactive');
      const showOverdue = getRouteQueryParam(route, 'showOverdue');
      const showSuppressed = getRouteQueryParam(route, 'showSuppressed');
      const searchString = getRouteQueryParam(route, 'searchString');
      return combineLatest$([
                              this.findAll(salesPersonId || filter.salesPersonId, {
                                showCompleted:  showCompleted || filter.showCompleted,
                                showActive: showActive || filter.showActive,
                                showInactive: showInactive || filter.showInactive,
                                showOverdue: showOverdue || filter.showOverdue,
                                showSuppressed: showSuppressed || filter.showSuppressed,
                                searchString }),
                              this.dictionaryService.getDictionaryFromGlobal(globalDictionaries.stage),
                              this.dictionaryService.getByEnum(ContactCustomerType),
                              this.dictionaryService.getByEnum(SalesFolderStatus),
                              this.findAllSalesPersons(),
                              this.dictionaryService.getDictionaryFromGlobal(globalDictionaries.notProceedingReasons)
                                .pipe(map$((res) => res.map(dictionaryItemFromDto))),
                            ]).pipe(map$(([
                                                          leadBoardList,
                                                         stages,
                                                         typeOfCustomerDict,
                                                         statusDict,
                                                         salespersonList,
                                                         notProceedingReasons,
                                                       ]) => ({
        leadBoardList,
        priorities: sortByStringField(priorities, 'localization').map((item) => ({name: item.localization, id: parseInt(item.itemCode)})),
        searchString,
        stages: sortByStringField(stages, 'itemCode').map((item) => ({name: item.localization, id: parseInt(item.itemCode) })),
        typeOfCustomerDict,
        statusDict,
        salespersonList,
        salesPersonId: this.geSalesPersonIdFromRoute(route),
        notProceedingReasons,
        filter : {
          ...filter,
          showActive: getBool(filter.showActive),
          showInactive: getBool(filter.showInactive),
          showOverdue: getBool(filter.showOverdue),
          showCompleted: getBool(filter.showCompleted),
          showSuppressed: getBool(filter.showSuppressed),
        },
                                                       })));
    }));
  }

  initDialogView(route: DialogRouteSnapshot<LeadBoardCardMoveDialogConfig>): any {
    return {
      priorities: this.dictionaryService.getDictionaryFromGlobal(globalDictionaries.priority)
        .pipe(map$((stages) => sortByStringField(stages, 'localization')
        .map((item) => ({name: item.localization, id: parseInt(item.itemCode)})))),
      stages: this.dictionaryService.getDictionaryFromGlobal(globalDictionaries.stage)
        .pipe(map$((stages) => sortByStringField(stages, 'itemCode')
          .map((item) => ({name: item.localization, id: parseInt(item.itemCode)})))),
    };
  }

  findAll(salespersonId: Maybe<ID>,
          filter: LeadBoardFilter = {
            showCompleted: false,
            showActive: false,
            showInactive: false,
            showOverdue: false,
            showSuppressed: false,
            searchString: '',
          }): Observable<LeadBoardListItem[]> {
    return this.restService.findAll(salespersonId, filter);
  }

  update(id: ID, params: LeadBoardListItemParams): Observable<LeadBoardListItemParams> {
    return this.restService.update(id, params);
  }

  findAllSalesPersons(): Observable<SalespersonSelectItem[]> {
    return this.userRestService.findAllSalesPersons(true).pipe(
      map$((userList: UserListItem[]) =>
        concat(
          [],
          ...reverse(
            partition(
              sortBy(
                map(userList, salespersonSelectItemMapper),
                'name',
              ),
              isSalespersonSelectItemInactive)))));
  }
  private geSalesPersonIdFromRoute(route: ActivatedRouteSnapshot): string {
    const params = getRouteQueryParams(route);
    if (params && params.filter) {
      const filter = JSON.parse(params.filter);
      if (filter && filter.salesPersonId) {
        return filter.salesPersonId;
      }
    }
    return '';
  }
}
